import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { slotMsFor, timeframeSwitchGoal } from '../utils/timeframeSwitch';

/**
 * The eased move between timeframes (DWLF-427), as props for the chart. On a timeframe switch the
 * chart is given the range that was on screen for one commit, then the new timeframe's window
 * (`timeframeSwitchGoal`), which the chart eases to; once it is shown, pan/zoom takes it over via
 * the handle's `setVisibleTimeRange`. A new symbol opens on its latest bars, with no move.
 *
 * `onSwitch(from)` runs in the render that first sees a new symbol or timeframe, before anything of
 * it is drawn, with the range the move starts from (null when there is no move).
 *
 * Returns `recordViewRange` for the chart's `onViewportChange`, and `visibleRange` and `onSettled`
 * for its `visibleRange` and `onVisibleRangeSettled`.
 */
export default function useTimeframeSwitchMove({
  symbol, timeframe, candleTimes, visibleBars, extraSlots, chartRef, onSwitch,
}) {
  const lastViewRangeRef = useRef(null);
  const goalRef = useRef(null);
  const [move, setMove] = useState(null); // { from, started }
  const [shownSeries, setShownSeries] = useState({ symbol, timeframe });

  if (shownSeries.symbol !== symbol || shownSeries.timeframe !== timeframe) {
    // Mid-move, start from where that move was going: while the chart holds a range it reports
    // no viewport changes, so the last one recorded is from before the earlier switch.
    const from = shownSeries.symbol !== symbol ? null : (move ? goalRef.current : lastViewRangeRef.current);
    setShownSeries({ symbol, timeframe });
    setMove(from ? { from, started: false } : null);
    onSwitch?.(from);
  }

  const goal = useMemo(
    () => (move
      ? timeframeSwitchGoal({
        from: move.from, times: candleTimes, slotMs: slotMsFor(timeframe), visibleBars, extraSlots,
      })
      : null),
    [move, candleTimes, timeframe, visibleBars, extraSlots],
  );
  goalRef.current = goal;

  // `from` is on screen for one commit, then the chart eases to the goal; no goal, no move.
  useEffect(() => {
    if (!move) {
      return;
    }
    if (!goal) {
      setMove(null);
    } else if (!move.started) {
      setMove({ ...move, started: true });
    }
  }, [move, goal]);

  const recordViewRange = useCallback((viewport) => {
    if (viewport && Number.isFinite(viewport.startTime) && Number.isFinite(viewport.endTime)) {
      lastViewRangeRef.current = { startTime: viewport.startTime, endTime: viewport.endTime };
    }
  }, []);

  const onSettled = useCallback(
    (range) => {
      if (!move?.started || !goal || range.startTime !== goal.startTime || range.endTime !== goal.endTime) {
        return;
      }
      chartRef.current?.setVisibleTimeRange?.(goal);
      setMove(null);
    },
    [move, goal, chartRef],
  );

  const visibleRange = move && goal ? (move.started ? goal : move.from) : undefined;
  return { recordViewRange, visibleRange, onSettled };
}
